import type { ReactNode } from 'react';
import { DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';

import { Badge, Button, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import { NHAN_TRANG_THAI, NHAN_VAI_TRO, TONG_TRANG_THAI, TONG_VAI_TRO } from '../../constants';
import { TRANG_THAI_NHIEM_VU, trangThaiHien, VAI_TRO_THAM_GIA } from '../../nghiep-vu/nhiem-vu';
import type { DonViDanhBa, NutNhiemVu, TrangThaiNhiemVu, VaiTroThamGia } from '../../types-nhiem-vu';
import { DeadlineChip } from './DeadlineChip';
import { IconAi } from './IconAi';
import { QuickAddParticipant } from './QuickAddParticipant';
import { TaskActivityPanel } from './TaskActivityPanel';
import type { QuyenNhiemVu } from './TaskTreeList';
import { UserAvatar } from './UserAvatar';

const RONG_NGAN = 560;

interface Props {
  /** `undefined` = không mở; `null` = đang mở nhưng chưa tải xong việc lấy riêng theo `?task=`. */
  task: NutNhiemVu | null | undefined;
  /** Việc mở theo `?task=` tải riêng mà lỗi (đã xoá, không có quyền). */
  loi: string | null;
  parentTitle: string | null;
  orgs: DonViDanhBa[];
  quyen: QuyenNhiemVu;
  statusPending: boolean;
  onClose: () => void;
  onOpenTask: (task: NutNhiemVu) => void;
  onAddChild: (parent: NutNhiemVu) => void;
  onDecompose: (parent: NutNhiemVu) => void;
  onEdit: (task: NutNhiemVu) => void;
  onDelete: (task: NutNhiemVu) => void;
  onStatus: (task: NutNhiemVu, status: TrangThaiNhiemVu) => void;
  onDownload: (task: NutNhiemVu) => void;
}

export function TaskDetailDrawer(p: Props) {
  const { t } = useT();
  const task = p.task;
  return (
    <Drawer
      open={task !== undefined}
      onClose={p.onClose}
      size={RONG_NGAN}
      title={t('vanBanNhiemVu.chiTiet.tieuDe')}
      destroyOnHidden
      extra={
        task && (
          <div className="flex gap-1">
            {p.quyen.canUpdate && (
              <Button variant="ghost" size="sm" icon={<EditOutlined />} onClick={() => p.onEdit(task)}>
                {t('vanBanNhiemVu.ds.sua')}
              </Button>
            )}
            {p.quyen.canDelete && (
              <Button
                variant="ghost"
                size="sm"
                icon={<DeleteOutlined />}
                aria-label={t('vanBanNhiemVu.ds.xoa')}
                onClick={() => p.onDelete(task)}
              />
            )}
          </div>
        )
      }
    >
      {task ? (
        <DetailBody {...p} task={task} />
      ) : p.loi ? (
        <p role="alert" className="text-sm text-danger-700">
          {t('vanBanNhiemVu.chiTiet.khongTai', { loi: p.loi })}
        </p>
      ) : (
        <div className="space-y-3">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
    </Drawer>
  );
}

function DetailBody({
  task,
  parentTitle,
  orgs,
  quyen,
  statusPending,
  onOpenTask,
  onAddChild,
  onDecompose,
  onStatus,
  onDownload,
}: Props & { task: NutNhiemVu }) {
  const { t, locale } = useT();
  const hien = trangThaiHien(task);
  const conDo = task.child_total - task.child_done;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        {parentTitle && (
          <p className="text-xs text-content-muted">
            {t('vanBanNhiemVu.chiTiet.thuoc')}: <span className="text-content-secondary">{parentTitle}</span>
          </p>
        )}
        <h2 className="text-base font-semibold leading-snug text-content-primary">{task.title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={TONG_TRANG_THAI[hien].badge}>{t(NHAN_TRANG_THAI[hien])}</Badge>
          <DeadlineChip task={task} />
        </div>
        {task.progress > 0 && task.child_total === 0 && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-subtle">
              <div className="h-full rounded-full bg-primary-500" style={{ width: `${task.progress}%` }} />
            </div>
            <span className="text-xs font-medium tabular-nums text-content-secondary">{task.progress}%</span>
          </div>
        )}
      </header>

      {quyen.canUpdate && (
        <section aria-label={t('vanBanNhiemVu.ds.cotTrangThai')} className="flex flex-wrap gap-2">
          {TRANG_THAI_NHIEM_VU.map((s) => {
            const biChan = s === 'HOAN_THANH' && conDo > 0;
            return (
              <Button
                key={s}
                size="sm"
                variant={s === task.status ? 'primary' : 'outline'}
                disabled={biChan || statusPending}
                aria-pressed={s === task.status}
                title={biChan ? t('vanBanNhiemVu.ds.xongBiChan', { so: conDo }) : undefined}
                onClick={() => s !== task.status && onStatus(task, s)}
              >
                {biChan ? t('vanBanNhiemVu.ds.xongBiChan', { so: conDo }) : t(NHAN_TRANG_THAI[s])}
              </Button>
            );
          })}
        </section>
      )}

      <section>
        <TieuDeMuc>{t('vanBanNhiemVu.chiTiet.moTa')}</TieuDeMuc>
        <p className="whitespace-pre-line text-sm text-content-secondary">
          {task.description || <span className="text-content-muted">{t('vanBanNhiemVu.chiTiet.chuaMoTa')}</span>}
        </p>
      </section>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Info label={t('vanBanNhiemVu.chiTiet.donViChuTri')} value={task.lead_org_name ?? t('vanBanNhiemVu.ds.chuaCoDonVi')} />
        <Info label={t('vanBanNhiemVu.chiTiet.nguoiGiao')} value={task.created_by_name ?? t('vanBanNhiemVu.chiTiet.khongRo')} />
        <Info label={t('vanBanNhiemVu.chiTiet.ngayGiao')} value={ngayGioVN(task.created_at, locale)} />
        {task.completed_at && <Info label={t('vanBanNhiemVu.chiTiet.xongLuc')} value={ngayGioVN(task.completed_at, locale)} />}
        {task.related_document && <Info label={t('vanBanNhiemVu.chiTiet.vanBanLienQuan')} value={task.related_document} />}
        {task.attachment_filename && (
          <div className="col-span-2">
            <dt className="text-xs text-content-muted">{t('vanBanNhiemVu.chiTiet.tepDinhKem')}</dt>
            <dd>
              <button
                type="button"
                aria-label={t('vanBanNhiemVu.chiTiet.taiTep', { ten: task.attachment_filename })}
                onClick={() => onDownload(task)}
                className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded border border-default px-2 py-1 text-xs text-content-link hover:border-primary-300"
              >
                <PaperClipOutlined aria-hidden />
                <span className="truncate">{task.attachment_filename}</span>
              </button>
            </dd>
          </div>
        )}
      </dl>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <TieuDeMuc>{t('vanBanNhiemVu.chiTiet.nguoiThucHien')}</TieuDeMuc>
          {quyen.canUpdate && <QuickAddParticipant task={task} orgs={orgs} />}
        </div>
        {task.participants.length === 0 ? (
          <p className="text-sm text-content-muted">{t('vanBanNhiemVu.chiTiet.chuaCoNguoi')}</p>
        ) : (
          <div className="space-y-3">
            {VAI_TRO_THAM_GIA.map((role) => (
              <RoleGroup key={role} role={role} task={task} />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-1">
          <TieuDeMuc>{t('vanBanNhiemVu.chiTiet.viecCon', { xong: task.child_done, tong: task.child_total })}</TieuDeMuc>
          {quyen.canCreate && (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" icon={<IconAi />} onClick={() => onDecompose(task)}>
                {t('vanBanNhiemVu.ds.aiPhanRa')}
              </Button>
              <Button variant="ghost" size="sm" icon={<PlusOutlined />} onClick={() => onAddChild(task)}>
                {t('vanBanNhiemVu.ds.giaoCon')}
              </Button>
            </div>
          )}
        </div>
        {task.children.length === 0 ? (
          <p className="text-sm text-content-muted">
            {task.child_total > 0
              ? t('vanBanNhiemVu.ds.tienDoCon', { xong: task.child_done, tong: task.child_total })
              : t('vanBanNhiemVu.chiTiet.chuaCoViecCon')}
          </p>
        ) : (
          <ul className="divide-y divide-default rounded-lg border border-default">
            {task.children.map((child) => {
              const cs = trangThaiHien(child);
              return (
                <li key={child.id}>
                  <button
                    type="button"
                    onClick={() => onOpenTask(child)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-subtle focus-visible:bg-subtle"
                  >
                    <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', TONG_TRANG_THAI[cs].dot)} />
                    <span className="min-w-0 flex-1 truncate text-sm text-content-primary">{child.title}</span>
                    <Badge variant={TONG_TRANG_THAI[cs].badge} className="shrink-0 whitespace-nowrap">
                      {t(NHAN_TRANG_THAI[cs])}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <TaskActivityPanel task={task} canWrite={quyen.canCreate} />
    </div>
  );
}

function RoleGroup({ role, task }: { role: VaiTroThamGia; task: NutNhiemVu }) {
  const { t } = useT();
  const nguoi = task.participants.filter((p) => p.role === role);
  if (nguoi.length === 0) return null;
  return (
    <div>
      <Badge variant={TONG_VAI_TRO[role]} className="mb-1.5">
        {t(NHAN_VAI_TRO[role])}
      </Badge>
      <ul className="space-y-1.5">
        {nguoi.map((p) => (
          <li key={p.user_id} className="flex items-center gap-2">
            <UserAvatar name={p.user_name} />
            <span className="min-w-0">
              <span className="block truncate text-sm text-content-primary">{p.user_name}</span>
              <span className="block truncate text-xs text-content-muted">
                {[p.org_name, p.user_email].filter(Boolean).join(' · ')}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TieuDeMuc({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">{children}</h3>;
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{label}</dt>
      <dd className="truncate text-content-primary" title={value}>
        {value}
      </dd>
    </div>
  );
}
